import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Spinner from '../components/Spinner'
import SegmentedControl from '../components/SegmentedControl'
import { Container } from '../components/Animations'
import Radom, { IOrgFees } from '../state/Radom'

import USDCLogo from '../images/USDCLogo.svg'
import TetherLogo from '../images/TetherLogo.svg'
import EUROCLogo from '../images/EUROCLogo.svg'
import BTCLogo from '../images/BTCLogo.svg'
import EthLogo from '../images/EthLogo.svg'
import SolLogo from '../images/Solana.svg'
import PolygonLogo from '../images/PolygonLogo.svg'
import TronLogo from '../images/TronLogo.svg'
import LitecoinLogo from '../images/LitecoinLogo.svg'
import DogeLogo from '../images/DogeLogo.svg'
import BCHLogo from '../images/BCHLogo.svg'
import AvalancheLogo from '../images/AvalancheLogo.svg'
import BatLogo from '../images/BAT.svg'
import DotLogo from '../images/DOT.svg'

import {
  SettingsCard,
  SettingsCardBody,
  SettingsHero,
  SettingsPage,
  SettingsRow,
  SettingsRowDescription,
  SettingsRows,
  SettingsRowTitle,
  SettingsSubtext,
  SettingsTitle
} from './Organization/SettingsLayout'

const COLS = '1fr 80px 130px 100px'

const FeeTableWrap = styled.div`
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e9eef5);
  border-radius: 14px;
  overflow: hidden;
`

const FeeTableHead = styled.div`
  display: grid;
  grid-template-columns: ${COLS};
  padding: 8px 20px;
  background: var(--surface-subtle, #fafbfd);
  border-bottom: 1px solid var(--border, #e7ecf3);
`

const FeeTableHeadCell = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: var(--text-faint, #9ba3b4);
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const FeeTableRow = styled.div<{ $last?: boolean, $expandable?: boolean }>`
  display: grid;
  grid-template-columns: ${COLS};
  padding: 13px 20px;
  align-items: center;
  border-bottom: ${({ $last }) => $last ? 'none' : '1px solid var(--hairline, #edf1f7)'};
  cursor: ${({ $expandable }) => $expandable ? 'pointer' : 'default'};

  &:hover {
    background: var(--surface-subtle, #fafbfd);
  }
`

const FeeMethod = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
  color: var(--ink, #20242d);
  gap: 6px;
`

const FeePercent = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: var(--ink, #20242d);
`

const FeeSecondary = styled.div`
  font-size: 12px;
  color: var(--text-soft, #7d8492);
`

const FreeText = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: var(--pill-green-ink, #2e7d32);
`

const Chevron = styled.svg<{ $open: boolean }>`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: var(--text-faint, #9ba3b4);
  transform: ${({ $open }) => $open ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.18s ease;
`

const PairsPanelOuter = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => $open ? '1fr' : '0fr'};
  transition: grid-template-rows 0.22s ease;
`

const PairsPanelInner = styled.div`
  min-height: 0;
  overflow: hidden;
`

const PairsPanel = styled.div<{ $last?: boolean }>`
  border-bottom: ${({ $last }) => $last ? 'none' : '1px solid var(--hairline, #edf1f7)'};
  background: var(--surface-subtle, #fafbfd);
  padding: 12px 20px 16px 20px;
`

const PairsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-flow: column;
  gap: 2px 0;

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const PairItem = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: var(--text-muted, #4a5263);
  padding: 5px 0;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
`

const PairIcons = styled.div`
  display: flex;
  align-items: center;
`

const PairIcon = styled.img<{ $second?: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid var(--surface, #fff);
  background: var(--surface, #fff);
  margin-left: ${({ $second }) => $second ? '-5px' : '0'};
  position: relative;
  z-index: ${({ $second }) => $second ? 1 : 2};
`

const COIN_LOGOS: Record<string, string> = {
  USDC: USDCLogo as string,
  USDT: TetherLogo as string,
  EURC: EUROCLogo as string,
  BTC: BTCLogo as string,
  ETH: EthLogo as string,
  SOL: SolLogo as string,
  POL: PolygonLogo as string,
  TRX: TronLogo as string,
  LTC: LitecoinLogo as string,
  DOGE: DogeLogo as string,
  BCH: BCHLogo as string,
  AVAX: AvalancheLogo as string,
  BAT: BatLogo as string,
  DOT: DotLogo as string
}

function formatPct(n: number): string {
  return `${parseFloat(n.toFixed(6))}%`
}

function pct(v?: string): string {
  if (!v) return '—'
  const n = parseFloat(v)
  return isNaN(n) || n === 0 ? '—' : formatPct(n * 100)
}

function usd(v?: string): string {
  if (!v || parseFloat(v) === 0) return ''
  return `$${parseFloat(v).toFixed(2)}`
}

function swapSecondary(fee: { feePercent: string, flatFeeUsd: string, minFeeUsd?: string }): string | undefined {
  if (fee.minFeeUsd && parseFloat(fee.minFeeUsd) > 0) return `min ${usd(fee.minFeeUsd)}`
  if (usd(fee.flatFeeUsd)) return `flat ${usd(fee.flatFeeUsd)}`
  return undefined
}

function flatAndPctSecondary(fee:
{ feePercent: string
  flatFeeAmount: string
  flatFeeCurrency: string }): string | undefined {
  const amount = usd(fee.flatFeeAmount)
  if (amount) return `flat ${amount} ${fee.flatFeeCurrency}`
  return undefined
}

interface FeeEntry {
  method: string
  feePercent: string
  flatMin?: string
  gasFee?: string
  free?: boolean
  pairs?: string[]
}

interface FeeSection {
  title: string
  rows: FeeEntry[]
}

function buildSections(fees: IOrgFees): FeeSection[] {
  const sections: FeeSection[] = []

  const payment: FeeEntry[] = []
  if (fees.base) {
    const gasFee = pct(fees.base.networkFeePercent)
    payment.push({ method: 'Crypto - Checkout Sessions', feePercent: pct(fees.base.feePercent), gasFee })
    if (fees.openBankingFee) {
      payment.push({
        method: 'Open Banking - Checkout Sessions',
        feePercent: pct(fees.openBankingFee.feePercent),
        flatMin: fees.openBankingFee.minFeeEur && parseFloat(fees.openBankingFee.minFeeEur) > 0
          ? `min €${parseFloat(fees.openBankingFee.minFeeEur).toFixed(2)}`
          : undefined
      })
    }
    payment.push({ method: 'Invoices', feePercent: pct(fees.base.feePercent), gasFee })
    payment.push({ method: 'Payment Links', feePercent: pct(fees.base.feePercent), gasFee })
    payment.push({ method: 'Payment Sessions', feePercent: pct(fees.base.feePercent), gasFee })
  } else if (fees.openBankingFee) {
    payment.push({
      method: 'Open Banking — Checkout Sessions',
      feePercent: pct(fees.openBankingFee.feePercent),
      flatMin: fees.openBankingFee.minFeeEur && parseFloat(fees.openBankingFee.minFeeEur) > 0
        ? `min €${parseFloat(fees.openBankingFee.minFeeEur).toFixed(2)}`
        : undefined
    })
  }
  if (payment.length > 0) sections.push({ title: 'Payment', rows: payment })

  const deposit: FeeEntry[] = []
  if (fees.deposit) deposit.push({ method: 'Crypto Deposit Account', feePercent: pct(fees.deposit.feePercent) })
  if (fees.virtualAccountAch) deposit.push({ method: 'Virtual Account — ACH', feePercent: pct(fees.virtualAccountAch.feePercent), flatMin: flatAndPctSecondary(fees.virtualAccountAch) })
  if (fees.virtualAccountFedwire) deposit.push({ method: 'Virtual Account — FedWire', feePercent: pct(fees.virtualAccountFedwire.feePercent), flatMin: flatAndPctSecondary(fees.virtualAccountFedwire) })
  if (fees.virtualAccountSepa) {
    deposit.push({ method: 'Virtual Account — SEPA', feePercent: pct(fees.virtualAccountSepa.feePercent), flatMin: flatAndPctSecondary(fees.virtualAccountSepa) })
    deposit.push({ method: 'Manual Bank Transfer — SEPA', feePercent: pct(fees.virtualAccountSepa.feePercent), flatMin: flatAndPctSecondary(fees.virtualAccountSepa) })
  }
  if (fees.virtualAccountPix) deposit.push({ method: 'Virtual Account — PIX', feePercent: pct(fees.virtualAccountPix.feePercent), flatMin: flatAndPctSecondary(fees.virtualAccountPix) })
  if (fees.virtualAccountSpei) deposit.push({ method: 'Virtual Account — SPEI', feePercent: pct(fees.virtualAccountSpei.feePercent), flatMin: flatAndPctSecondary(fees.virtualAccountSpei) })
  if (fees.virtualAccountConversion) deposit.push({ method: 'Virtual Account — Stablecoin Conversion', feePercent: pct(fees.virtualAccountConversion.feePercent), flatMin: flatAndPctSecondary(fees.virtualAccountConversion) })
  if (deposit.length > 0) sections.push({ title: 'Deposit', rows: deposit })

  const withdrawal: FeeEntry[] = [
    { method: 'Crypto Withdrawals', feePercent: 'Free', free: true }
  ]
  if (fees.ach) withdrawal.push({ method: 'ACH Withdrawal', feePercent: pct(fees.ach.feePercent), flatMin: swapSecondary(fees.ach) })
  if (fees.sepa) withdrawal.push({ method: 'SEPA Withdrawal (B2B)', feePercent: pct(fees.sepa.feePercent), flatMin: swapSecondary(fees.sepa) })
  if (fees.pix) withdrawal.push({ method: 'PIX Withdrawal', feePercent: pct(fees.pix.feePercent), flatMin: swapSecondary(fees.pix) })
  if (fees.spei) withdrawal.push({ method: 'SPEI Withdrawal', feePercent: pct(fees.spei.feePercent), flatMin: swapSecondary(fees.spei) })
  sections.push({ title: 'Withdrawal', rows: withdrawal })

  const payout: FeeEntry[] = []
  if (fees.base) {
    payout.push({ method: 'Single Payouts', feePercent: pct(fees.base.feePercent) })
    payout.push({ method: 'Mass Payouts', feePercent: pct(fees.base.feePercent) })
  }
  if (fees.sepaB2c) payout.push({ method: 'SEPA Payouts (B2C)', feePercent: pct(fees.sepaB2c.feePercent), flatMin: swapSecondary(fees.sepaB2c) })
  if (payout.length > 0) sections.push({ title: 'Payout', rows: payout })


  return sections
}

const FeesSettings = observer((): React.ReactElement => {
  const [feesLoading, setFeesLoading] = useState(true)
  const [fees, setFees] = useState<IOrgFees | undefined>()
  const [activeTab, setActiveTab] = useState<string>('')
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  useEffect(() => {
    setFeesLoading(true)
    Radom.getOrgFees()
      .then(f => setFees(f))
      .catch(err => console.error('Failed to load fees', err))
      .finally(() => setFeesLoading(false))
  }, [])

  const sections = fees ? buildSections(fees) : []
  const tabLabels = sections.map(s => s.title)
  const effectiveTab = tabLabels.includes(activeTab) ? activeTab : tabLabels[0] ?? ''
  const activeSection = sections.find(s => s.title === effectiveTab)

  function toggleRow(method: string): void {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(method)) next.delete(method)
      else next.add(method)
      return next
    })
  }

  const rows = activeSection?.rows ?? []

  return (
    <Container>
      <SettingsPage>
        <SettingsHero>
          <div>
            <SettingsTitle>Fees</SettingsTitle>
            <SettingsSubtext>
              The fees configured for your organization by Radom. Contact support if you have questions.
            </SettingsSubtext>
          </div>
        </SettingsHero>

        {!feesLoading && sections.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 4 }}>
            <SegmentedControl
              tabs={tabLabels}
              selectedTab={effectiveTab}
              onTabChange={setActiveTab}
            />
          </div>
        )}

        <SettingsCard>
          {feesLoading
            ? <SettingsCardBody><Spinner /></SettingsCardBody>
            : sections.length === 0
              ? <SettingsCardBody>
                <SettingsRows>
                  <SettingsRow>
                    <div>
                      <SettingsRowTitle>No fees configured</SettingsRowTitle>
                      <SettingsRowDescription>
                        Default rates apply. Contact Radom support for details.
                      </SettingsRowDescription>
                    </div>
                  </SettingsRow>
                </SettingsRows>
              </SettingsCardBody>
              : <FeeTableWrap>
                <FeeTableHead>
                  <FeeTableHeadCell>Product / Method</FeeTableHeadCell>
                  <FeeTableHeadCell>% Fee</FeeTableHeadCell>
                  <FeeTableHeadCell>Flat / Min Fee</FeeTableHeadCell>
                  <FeeTableHeadCell>Gas Fee</FeeTableHeadCell>
                </FeeTableHead>
                {rows.map((row, i) => {
                  const isExpanded = expandedRows.has(row.method)
                  const isLast = i === rows.length - 1
                  return (
                    <React.Fragment key={row.method}>
                      <FeeTableRow
                        $last={isLast && !(isExpanded && !!row.pairs)}
                        $expandable={!!row.pairs}
                        onClick={row.pairs ? () => toggleRow(row.method) : undefined}
                      >
                        <FeeMethod>
                          {row.pairs && (
                            <Chevron $open={isExpanded} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </Chevron>
                          )}
                          {row.method}
                        </FeeMethod>
                        {row.free
                          ? <FreeText>Free</FreeText>
                          : <FeePercent>{row.feePercent}</FeePercent>
                        }
                        <FeeSecondary>{row.flatMin ?? '-'}</FeeSecondary>
                        <FeeSecondary>{row.gasFee ?? '-'}</FeeSecondary>
                      </FeeTableRow>
                      {row.pairs && (
                        <PairsPanelOuter $open={isExpanded}>
                          <PairsPanelInner>
                            <PairsPanel $last={isLast}>
                              <PairsGrid style={{ gridTemplateRows: `repeat(${Math.ceil(row.pairs.length / 4)}, auto)` }}>
                                {row.pairs.map(pair => {
                                  const [a, b] = pair.split('-')
                                  return (
                                    <PairItem key={pair}>
                                      <PairIcons>
                                        {COIN_LOGOS[a] && <PairIcon src={COIN_LOGOS[a]} alt={a} />}
                                        {COIN_LOGOS[b] && <PairIcon src={COIN_LOGOS[b]} alt={b} $second />}
                                      </PairIcons>
                                      {pair}
                                    </PairItem>
                                  )
                                })}
                              </PairsGrid>
                            </PairsPanel>
                          </PairsPanelInner>
                        </PairsPanelOuter>
                      )}
                    </React.Fragment>
                  )
                })}
              </FeeTableWrap>
          }
        </SettingsCard>
      </SettingsPage>
    </Container>
  )
})

export default FeesSettings
